import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, fmtError } from "@/lib/api";
import { L, fmtDate } from "@/constants/labels";
import { AdminPhotoModeration } from "@/components/AdminPhotoModeration";
import {
  CheckCircle2, XCircle, ExternalLink, Copy, AlertTriangle, Clock, FileText,
  ChevronDown, ChevronRight,
} from "lucide-react";

// File admin — pièces en `a_controler` uniquement, tri ancienneté ASC.
// Chantier 2 (2026-09) : boutons Valider / Refuser (modal motif) /
// Ouvrir URSSAF pour les attestations de vigilance. Aperçu des champs
// `ocr_extracted` affiché en colonne dédiée pour trier sans ouvrir le PDF.

const TYPE_LABEL = {
  kbis: "KBIS",
  urssaf: "URSSAF",
  rc_pro: "RC Pro",
  decennale: "Décennale",
  attestation_fiscale: "Att. fiscale",
};

export default function AdminDocumentsQueue() {
  const [state, setState] = useState({ docs: [], queue_size: 0, alert: false, threshold: 20 });
  const [loading, setLoading] = useState(true);
  const [refusalMotifs, setRefusalMotifs] = useState([]);
  const [refusing, setRefusing] = useState(null); // doc en cours de refus
  const [expandedId, setExpandedId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [q, cfg] = await Promise.all([
        api.get("/admin/documents/queue"),
        api.get("/documents/config"),
      ]);
      setState(q.data);
      setRefusalMotifs(cfg.data?.refusal_motifs || []);
    } catch (err) {
      toast.error(fmtError(err));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const valider = async (doc, method = "admin_manual") => {
    try {
      await api.post(`/admin/documents/${doc.id}/validate`, {
        statut: "verifie", validation_method: method,
      });
      toast.success(`${TYPE_LABEL[doc.type]} validé`);
      load();
    } catch (err) { toast.error(fmtError(err)); }
  };

  const refuser = async (doc, motif) => {
    try {
      await api.post(`/admin/documents/${doc.id}/validate`, {
        statut: "refuse", motif, validation_method: "admin_manual",
      });
      toast.success("Document refusé — l'artisan est notifié");
      setRefusing(null);
      load();
    } catch (err) { toast.error(fmtError(err)); }
  };

  return (
    <div className="max-w-6xl">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
        <h1 className="text-2xl md:text-3xl font-semibold text-[#1F2933]">
          File de vérification des documents
        </h1>
        <div className="flex items-center gap-3">
          <span className={`inline-flex items-center gap-2 rounded-full px-3 py-1 text-sm font-semibold border ${
            state.alert
              ? "bg-[#FEF2F2] border-[#FCA5A5] text-[#B91C1C]"
              : "bg-[#F0FDF4] border-[#86EFAC] text-[#166534]"
          }`} data-testid="queue-counter">
            {state.alert ? <AlertTriangle size={14} /> : <CheckCircle2 size={14} />}
            {state.queue_size} pièce{state.queue_size > 1 ? "s" : ""} à contrôler
          </span>
          {state.alert && (
            <span className="text-xs text-[#B91C1C]" data-testid="queue-alert">
              Seuil {state.threshold} dépassé — priorisez le traitement
            </span>
          )}
        </div>
      </div>

      {loading && <div className="card p-6 text-center text-[#3E4C59]">{L.common.loading}</div>}
      {!loading && state.docs.length === 0 && (
        <div className="card p-6 text-center text-[#3E4C59]" data-testid="queue-empty">
          <CheckCircle2 size={32} className="mx-auto text-[#22C55E] mb-2" />
          Aucune pièce en attente. Bien joué.
        </div>
      )}

      <div className="space-y-3">
        {state.docs.map(doc => (
          <QueueRow
            key={doc.id}
            doc={doc}
            expanded={expandedId === doc.id}
            onToggle={() => setExpandedId(expandedId === doc.id ? null : doc.id)}
            onValider={() => valider(doc)}
            onRefuser={() => setRefusing(doc)}
          />
        ))}
      </div>

      <AdminPhotoModeration />

      {refusing && (
        <RefusalModal
          doc={refusing}
          motifs={refusalMotifs}
          onCancel={() => setRefusing(null)}
          onConfirm={(motif) => refuser(refusing, motif)}
        />
      )}
    </div>
  );
}

function QueueRow({ doc, expanded, onToggle, onValider, onRefuser }) {
  const daysWaiting = Math.floor((new Date() - new Date(doc.created_at)) / 86400000);
  const isUrssaf = doc.type === "urssaf";
  const ocr = doc.ocr_extracted || {};
  const ocrFailed = doc.ocr_failed;
  const ocrMotif = doc.ocr_motif;

  return (
    <div className="card p-4" data-testid={`queue-row-${doc.id}`}>
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex-1 min-w-[200px]">
          <div className="flex items-center gap-2">
            <button onClick={onToggle} className="p-1 hover:bg-[#F5F6F8] rounded" data-testid={`queue-expand-${doc.id}`}>
              {expanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
            </button>
            <FileText size={16} className="text-[#3E4C59]" />
            <span className="font-semibold text-[#1F2933]">{TYPE_LABEL[doc.type] || doc.type}</span>
            <span className="text-sm text-[#3E4C59]">— {doc.user_nom || "?"}</span>
            <span className="text-xs text-[#6B7280]">SIRET {doc.user_siret || "?"}</span>
          </div>
          <div className="mt-1 text-xs text-[#3E4C59] flex items-center gap-3 flex-wrap">
            <span className="inline-flex items-center gap-1"><Clock size={11} /> Déposé il y a {daysWaiting} j</span>
            <span>Expire {fmtDate(doc.date_expiration)}</span>
            {ocrFailed && (
              <span className="inline-flex items-center gap-1 text-[#B91C1C]" data-testid={`queue-ocr-failed-${doc.id}`}>
                <AlertTriangle size={11} /> OCR échoué — {ocrMotif || "extraction impossible"}
              </span>
            )}
            {!ocrFailed && ocrMotif && (
              <span className="inline-flex items-center gap-1 text-[#92400E]">
                <AlertTriangle size={11} /> {ocrMotif}
              </span>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          {doc.signed_url && (
            <a href={doc.signed_url} target="_blank" rel="noreferrer"
                className="btn-outline text-xs" data-testid={`queue-open-${doc.id}`}>
              <ExternalLink size={12} /> Ouvrir le PDF
            </a>
          )}
          {isUrssaf && (
            <UrssafButton docId={doc.id} codeSecurite={ocr.code_securite} />
          )}
          <button onClick={onValider} className="btn-primary text-xs"
                   data-testid={`queue-valider-${doc.id}`}>
            <CheckCircle2 size={12} /> Valider
          </button>
          <button onClick={onRefuser}
                   className="inline-flex items-center gap-1 rounded px-3 py-1.5 text-xs font-medium bg-[#FEE2E2] text-[#B91C1C] hover:bg-[#FECACA] border border-[#FCA5A5]"
                   data-testid={`queue-refuser-${doc.id}`}>
            <XCircle size={12} /> Refuser
          </button>
        </div>
      </div>

      {expanded && (
        <OcrPreview doc={doc} />
      )}
    </div>
  );
}

// Aperçu des champs OCR — affichés à côté du PDF pour trier sans ouvrir.
// L'amélioration validée par l'utilisateur (Chantier 2, 2026-09).
function OcrPreview({ doc }) {
  const ocr = doc.ocr_extracted || {};
  const rows = [];
  if (doc.type === "kbis") {
    rows.push(["SIRET détecté", ocr.siret]);
    rows.push(["Date d'immatriculation", ocr.date_immatriculation]);
    if (ocr.api_check) {
      const ac = ocr.api_check;
      rows.push([
        "Vérification INSEE",
        ac.found
          ? (ac.actif ? `Actif — ${ac.denomination || "?"}` : `Non actif — ${ac.reason || "?"}`)
          : `Introuvable (${ac.reason || "?"})`,
      ]);
    }
  } else if (doc.type === "urssaf") {
    rows.push(["Code de sécurité", ocr.code_securite]);
    rows.push(["SIRET détecté", ocr.siret]);
    rows.push(["Date d'émission", ocr.date_emission]);
    rows.push(["Fin de validité", ocr.date_validite]);
  } else if (doc.type === "rc_pro" || doc.type === "decennale") {
    rows.push(["Assureur", ocr.assureur]);
    rows.push(["N° police", ocr.n_police]);
    rows.push(["Début de couverture", ocr.date_debut]);
    rows.push(["Fin de couverture", ocr.date_fin]);
  } else if (doc.type === "attestation_fiscale") {
    rows.push(["SIRET détecté", ocr.siret]);
    rows.push(["Début de période", ocr.periode_debut]);
    rows.push(["Fin de période", ocr.periode_fin]);
  }

  const meta = [
    ["Méthode d'extraction", doc.ocr_method || "—"],
    ["Traité le", doc.ocr_processed_at ? fmtDate(doc.ocr_processed_at) : "—"],
  ];

  return (
    <div className="mt-3 pt-3 border-t border-[#E4E7EB] grid grid-cols-1 md:grid-cols-2 gap-4" data-testid={`queue-ocr-preview-${doc.id}`}>
      <div>
        <div className="text-xs uppercase tracking-wider text-[#3E4C59] font-semibold mb-2">Champs détectés par l'OCR</div>
        <dl className="text-sm space-y-1">
          {rows.map(([label, value], i) => (
            <div key={i} className="flex justify-between gap-2 border-b border-[#F1F3F5] pb-1">
              <dt className="text-[#3E4C59]">{label}</dt>
              <dd className={`font-mono text-xs ${value ? "text-[#1F2933]" : "text-[#B91C1C]"}`}>{value || "—"}</dd>
            </div>
          ))}
        </dl>
      </div>
      <div>
        <div className="text-xs uppercase tracking-wider text-[#3E4C59] font-semibold mb-2">Métadonnées</div>
        <dl className="text-sm space-y-1">
          {meta.map(([label, value], i) => (
            <div key={i} className="flex justify-between gap-2 border-b border-[#F1F3F5] pb-1">
              <dt className="text-[#3E4C59]">{label}</dt>
              <dd className="text-xs text-[#1F2933]">{value}</dd>
            </div>
          ))}
        </dl>
      </div>
    </div>
  );
}

function UrssafButton({ docId, codeSecurite }) {
  const [link, setLink] = useState(null);
  const openLink = async () => {
    try {
      const { data } = await api.get(`/admin/documents/${docId}/urssaf-link`);
      setLink(data);
    } catch (err) { toast.error(fmtError(err)); }
  };
  const copyCode = () => {
    if (!link?.code_securite) return;
    navigator.clipboard.writeText(link.code_securite);
    toast.success("Code copié dans le presse-papiers");
  };

  if (!link) {
    return (
      <button onClick={openLink} className="btn-outline text-xs" data-testid={`queue-urssaf-${docId}`}>
        <ExternalLink size={12} /> Ouvrir URSSAF
      </button>
    );
  }
  return (
    <div className="flex items-center gap-1 flex-wrap" data-testid={`queue-urssaf-link-${docId}`}>
      <a href={link.verification_url} target="_blank" rel="noreferrer"
          className="btn-outline text-xs">
        <ExternalLink size={12} /> URSSAF
      </a>
      {link.code_securite ? (
        <button onClick={copyCode}
                 className="inline-flex items-center gap-1 rounded px-2 py-1 text-xs font-mono bg-[#F5F6F8] hover:bg-[#E4E7EB] border border-[#E4E7EB]"
                 title="Cliquez pour copier le code de sécurité"
                 data-testid={`queue-urssaf-copy-${docId}`}>
          <Copy size={11} /> {link.code_securite}
        </button>
      ) : (
        <span className="text-xs text-[#B91C1C]">Code non extrait — à lire sur le PDF</span>
      )}
    </div>
  );
}

function RefusalModal({ doc, motifs, onCancel, onConfirm }) {
  const [selectedCode, setSelectedCode] = useState(motifs[0]?.code || "autre");
  const [libre, setLibre] = useState("");

  const selectedMotif = motifs.find(m => m.code === selectedCode);
  const isAutre = selectedCode === "autre";
  const canSubmit = isAutre ? libre.trim().length >= 5 : true;

  const submit = () => {
    if (!canSubmit) return;
    // Concatène motif prédéfini + précision libre (si présente).
    const base = selectedMotif?.label || "";
    const final = libre.trim()
      ? (isAutre ? libre.trim() : `${base} ${libre.trim()}`)
      : base;
    onConfirm(final);
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" data-testid="refusal-modal">
      <div className="bg-white rounded-lg shadow-xl max-w-lg w-full p-6">
        <h2 className="text-lg font-semibold text-[#1F2933]">
          Refuser le document {TYPE_LABEL[doc.type] || doc.type}
        </h2>
        <p className="text-sm text-[#3E4C59] mt-1">
          Un e-mail sera envoyé à l'artisan avec le motif ci-dessous.
        </p>
        <div className="mt-4 space-y-2">
          {motifs.map(m => (
            <label key={m.code} className="flex items-start gap-2 text-sm cursor-pointer p-2 rounded hover:bg-[#F5F6F8]">
              <input type="radio" name="motif" value={m.code}
                      checked={selectedCode === m.code}
                      onChange={() => setSelectedCode(m.code)}
                      data-testid={`refusal-motif-${m.code}`} />
              <span className="text-[#1F2933]">{m.label}</span>
            </label>
          ))}
        </div>
        <div className="mt-3">
          <label className="label">Précision {isAutre ? "(obligatoire)" : "(facultatif)"}</label>
          <textarea rows={3} value={libre} onChange={e => setLibre(e.target.value)}
                     className="input font-mono text-xs"
                     placeholder={isAutre ? "Décrivez précisément le problème." : "Optionnel : détail complémentaire."}
                     data-testid="refusal-libre" />
        </div>
        <div className="mt-5 flex justify-end gap-2">
          <button onClick={onCancel} className="btn-outline" data-testid="refusal-cancel">Annuler</button>
          <button onClick={submit} disabled={!canSubmit}
                   className={`btn-primary ${!canSubmit ? "opacity-50 cursor-not-allowed" : ""}`}
                   data-testid="refusal-confirm">
            Refuser et notifier
          </button>
        </div>
      </div>
    </div>
  );
}
